import { create } from 'zustand';
import httpClient from '../lib/httpClient';
import { ENDPOINTS } from '../services/api/endpoints';

const CACHE_TTL = 5 * 60 * 1000; // 5 minutes in ms

// Most restrictive state, applied when the API call fails.
// Support / News / Checkout stay visible as baseline services (Stage 1 items).
const FALLBACK_VISIBILITY = {
  invoices: { data: { isVisible: false, isDisabled: false } },
  quotations: { data: { isVisible: false, isDisabled: false } },
  subscriptions: { data: { isVisible: false, isDisabled: false } },
  checkout: { data: { isVisible: true, isDisabled: false } },
  dashboard: { data: { isVisible: false, isDisabled: false } },
  users: { data: { isVisible: false, isDisabled: false } },
  downloads: { data: { isVisible: false, isDisabled: false } },
  datanet: { data: { isVisible: false, isDisabled: false } },
  support: { data: { isVisible: true, isDisabled: false } },
  news: { data: { isVisible: true, isDisabled: false } },
  success: false,
  errorMessage: 'Unable to load sidebar visibility data',
};

// In-flight request guard (module scoped, not reactive state).
// Prevents duplicate API calls from StrictMode double-mount or concurrent callers.
let inflightRequest = null;

export const useSidebarVisibilityStore = create((set, get) => ({
  // ────────── STATE ──────────
  // Per-section visibility data
  invoices: null,
  quotations: null,
  subscriptions: null,
  checkout: null,
  dashboard: null,
  users: null,
  downloads: null,
  datanet: null,
  support: null,
  news: null,

  // Overall status
  success: false,
  errorMessage: null,

  // UI State
  loading: false,
  error: null,
  lastFetched: null,
  // The active HaloPSA site the cached visibility was computed for. Visibility is per-site (the
  // backend resolves it from the active site's roles), so a site switch must refetch even within the
  // TTL — a stale hit would show the previous site's sidebar.
  lastFetchedSiteId: null,

  // ────────── ACTIONS ──────────

  // Set all data from API response (per-section structure)
  setData: (response) => set({
    invoices: response.invoices || null,
    quotations: response.quotations || null,
    subscriptions: response.subscriptions || null,
    checkout: response.checkout || null,
    dashboard: response.dashboard || null,
    users: response.users || null,
    downloads: response.downloads || null,
    // BE serializes this section as `dataNet` (camelCase)
    datanet: response.dataNet || response.datanet || null,
    support: response.support || null,
    news: response.news || null,
    success: response.success || false,
    errorMessage: response.errorMessage || null,
    lastFetched: Date.now(),
  }),

  // Fetch visibility from the API. No-op while cached data is fresh,
  // unless `force` is set. Concurrent calls share one request.
  fetchVisibility: async ({ force = false, siteId } = {}) => {
    const { lastFetched, lastFetchedSiteId } = get();
    // A site switch must refetch even within the TTL: the cached visibility was computed from the
    // previous site's roles. `siteId === undefined` means the caller didn't pass one (e.g. refresh()
    // on a lifecycle event) — keep TTL-only behaviour; an explicit value (null for single-site, or a
    // number) is compared against the last-fetched site.
    const siteChanged = siteId !== undefined && siteId !== lastFetchedSiteId;
    const isFresh = lastFetched && !siteChanged && Date.now() - lastFetched < CACHE_TTL;
    if (!force && isFresh) return;
    if (inflightRequest) return inflightRequest;

    inflightRequest = (async () => {
      // Only surface the loading flag on the very first load —
      // refreshes keep showing current data until new data lands.
      set({ loading: !get().lastFetched, error: null });

      try {
        const response = await httpClient.get(ENDPOINTS.SIDEBAR.VISIBILITY);
        const data = response.data?.data || response.data;
        get().setData(data);
        // Stamp the site only when the caller supplied one, so a plain refresh() doesn't clobber it.
        set({
          loading: false,
          lastFetchedSiteId: siteId !== undefined ? siteId : get().lastFetchedSiteId,
        });
      } catch (error) {
        console.error('Failed to fetch sidebar visibility:', error);
        get().setData(FALLBACK_VISIBILITY);
        // lastFetched stays null so the next mount retries instead of caching the fallback
        set({ error: error.message, loading: false, lastFetched: null });
      } finally {
        inflightRequest = null;
      }
    })();

    return inflightRequest;
  },

  // Force refetch after a lifecycle-changing action
  // (quote created, invoice generated, payment received, firm switched).
  refresh: () => get().fetchVisibility({ force: true }),

  // ────────── GETTERS ──────────

  // Get visibility for a specific menu item
  getItemVisibility: (itemId) => {
    const section = get()[itemId];
    return section?.data || { isVisible: false, isDisabled: false };
  },

  // Check if item is visible
  isItemVisible: (itemId) => {
    const section = get()[itemId];
    return section?.data?.isVisible || false;
  },

  // Check if item is disabled
  isItemDisabled: (itemId) => {
    const section = get()[itemId];
    return section?.data?.isDisabled || false;
  },

  // Clear all data (call on logout)
  clear: () => set({
    invoices: null,
    quotations: null,
    subscriptions: null,
    checkout: null,
    dashboard: null,
    users: null,
    downloads: null,
    datanet: null,
    support: null,
    news: null,
    success: false,
    errorMessage: null,
    lastFetched: null,
    lastFetchedSiteId: null,
    loading: false,
    error: null,
  }),
}));

// Dev-only helper: expose the store in the browser console so lifecycle states
// can be simulated without changing backend data, e.g.:
//   __sidebarVisibility.setState({ downloads: { data: { isVisible: true, isDisabled: true } } })
// Stripped from production builds (import.meta.env.DEV is false there).
if (import.meta.env.DEV && globalThis.window !== undefined) {
  globalThis.__sidebarVisibility = useSidebarVisibilityStore;
}
